let sessionPassword: string | null = null;
let verificationPromise: Promise<void> | null = null;

const CONNECTIVITY_CHANGED_EVENT = "ma-cave:connectivity-changed";
export const AUTHENTICATION_REQUIRED_EVENT = "ma-cave:authentication-required";

function reportNetworkFailure(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(CONNECTIVITY_CHANGED_EVENT, { detail: { online: false } }),
  );
}

function reportAuthenticationRequired(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(AUTHENTICATION_REQUIRED_EVENT));
}

async function sessionFetch(
  input: RequestInfo | URL,
  init: RequestInit,
): Promise<Response> {
  try {
    return await fetch(input, init);
  } catch (error) {
    reportNetworkFailure();
    throw error;
  }
}

export class RemoteAuthenticationError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "RemoteAuthenticationError";
    this.status = status;
  }
}

export function rememberSessionPassword(password: string): void {
  if (sessionPassword !== password) verificationPromise = null;
  sessionPassword = password;
}

export function forgetSessionPassword(): void {
  sessionPassword = null;
  verificationPromise = null;
}

async function verificationError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as {
      erreur?: { message?: string };
      error?: string;
    };
    return body.erreur?.message ?? body.error ?? "Authentification impossible.";
  } catch {
    return "Authentification impossible.";
  }
}

export async function verifyRemotePassword(
  password: string,
  trustDevice = false,
): Promise<void> {
  if (!password || password.length > 1024) {
    throw new RemoteAuthenticationError("Le mot de passe est requis.", 400);
  }
  let response: Response;
  try {
    response = await sessionFetch("/_auth/verify", {
      method: "POST",
      cache: "no-store",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ password, trust_device: trustDevice }),
    });
  } catch {
    throw new RemoteAuthenticationError(
      "Connexion au serveur impossible. Vérifiez le réseau.",
      0,
    );
  }
  if (!response.ok) {
    throw new RemoteAuthenticationError(
      await verificationError(response),
      response.status,
    );
  }
}

async function renewSession(): Promise<void> {
  if (!sessionPassword) {
    throw new RemoteAuthenticationError("Authentification requise.", 401);
  }
  if (verificationPromise) return verificationPromise;

  const password = sessionPassword;
  const pending = verifyRemotePassword(password, false);
  verificationPromise = pending;
  try {
    await pending;
  } finally {
    // Un verrouillage ou un nouveau mot de passe peut avoir lancé une autre
    // vérification entre-temps. Une ancienne requête ne doit pas l'effacer.
    if (verificationPromise === pending) verificationPromise = null;
  }
}

export async function fetchWithSessionRetry(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const requestInit: RequestInit = {
    ...init,
    cache: "no-store",
    credentials: "same-origin",
  };
  // Préparer les deux clones avant le premier fetch : un objet Request portant
  // un corps devient consommé après l'envoi et ne pourrait plus être réessayé.
  const firstInput = input instanceof Request ? input.clone() : input;
  const retryInput = input instanceof Request ? input.clone() : input;
  let response = await sessionFetch(firstInput, requestInit);
  if (response.status !== 401) return response;
  if (!sessionPassword) {
    reportAuthenticationRequired();
    return response;
  }

  try {
    await renewSession();
  } catch (error) {
    if (error instanceof RemoteAuthenticationError && error.status === 401) {
      forgetSessionPassword();
      reportAuthenticationRequired();
    }
    throw error;
  }
  response = await sessionFetch(retryInput, requestInit);
  if (response.status === 401) {
    forgetSessionPassword();
    reportAuthenticationRequired();
  }
  return response;
}

export async function revokeTrustedSession(): Promise<boolean> {
  try {
    const response = await sessionFetch("/api/session", {
      method: "DELETE",
      cache: "no-store",
      credentials: "same-origin",
      keepalive: true,
      headers: { Accept: "application/json" },
    });
    return response.ok;
  } catch {
    return false;
  }
}
